import moment from 'moment-timezone';
import React, { useContext, useMemo, useState } from 'react';
import { useAppDispatch } from '../../../store';
import dayjs, { Dayjs } from 'dayjs';
import { bookingApi } from '../../../api/booking';
import { BookingSlots } from '../../../interfaces/booking_schedule';
import { RadioGroup } from '@headlessui/react';
import { message, Spin } from 'antd';
import ThemedDatePicker from '../../components/ThemedDatePicker';
import { LoadingIcon } from '../../components/Spinner';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import { CheckoutContext } from '../../Providers/CheckoutContext';
import {
  setFulfillmentDetails,
  setPickupDetails,
} from '../../../features/cart/cart';
import { useParams } from '../../../navigation';
import store from '../../../store';

type Props = {
  onSelect: (selectedSlot: BookingSlots) => void;
  onCancel: () => void;
  className?: string;
  fromCheckout?: boolean;
};

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export function PickupSchedulingSlot({
  onSelect,
  onCancel,
  className,
  fromCheckout = false,
}: Props) {
  const [bookingDate, setBookingDate] = useState<Dayjs>();
  const [bookingSlots, setBookingSlots] = useState<BookingSlots[]>([]);
  const [selectedSlot, setSelectSlot] = useState<BookingSlots | null>(null);
  const [fetchSlots, setFetchingSlots] = useState(false);
  const dispatch = useAppDispatch();
  const currentCheckout = useContext(CheckoutContext);

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { r_id } = useParams();

  const fetchAvailableSlots = async () => {
    try {
      setSelectSlot(null);
      setFetchingSlots(true);
      const response = await bookingApi.getCheckoutPickupSlots(
        currentCheckout?.id,
        bookingDate.format('YYYY-MM-DD'),
      );
      setBookingSlots(
        response.data?.filter(
          b =>
            moment(b.date + ' ' + b.start_at, 'YYYY-MM-DD HH:mm:ss') >
            moment().add(
              currentCheckout?.pickup_settings?.ordering_buffer_time ?? 20,
              'minutes',
            ),
        ),
      );
      setFetchingSlots(false);
    } catch (e) {
      setFetchingSlots(false);
      message.error(
        'Unable to fetch the scheduled pickup slots. Please try again later.',
      );
    }
  };

  useMemo(() => {
    if (bookingDate) {
      fetchAvailableSlots();
    }
  }, [bookingDate]);

  return (
    <div className={`bg-surface  relative min-h-screen `}>
      <div className="bg-primary/10 rounded-md px-2 py-2 border border-outline-variant mb-4 pb-24">
        <div className="mb-4 flex flex-col items-start">
          <div className="flex flex-row justify-left content-center items-start mb-2">
            <label
              className=" block uppercase tracking-wide text-on-surface text-sm font-bold"
              htmlFor="booking_date_input"
            >
              Pickup Order Date
            </label>
          </div>
          <ThemedDatePicker
            value={bookingDate}
            format="MM/DD/YYYY"
            className="w-full"
            inputReadOnly
            id={'booking_date_input'}
            onChange={date => {
              setBookingDate(date ?? undefined);
            }}
            disabledDate={day => {
              const minDate = dayjs().add(
                currentCheckout?.pickup_settings?.minimum_days_from_ordering ??
                  0,
                'day',
              );
              const maxDate = dayjs()
                .add(
                  currentCheckout?.scheduled_ordering_enabled
                    ? currentCheckout?.pickup_settings
                        ?.maximum_days_from_ordering ?? 1
                    : 0,
                  'day',
                )
                .endOf('day');
              let is_blackout = false;
              currentCheckout?.pickup_settings?.blackout_dates?.forEach(i => {
                if (
                  moment.utc(i).tz(tz).format('YYYY-MM-DD') ==
                  day.format('YYYY-MM-DD')
                ) {
                  is_blackout = true;
                }
              });
              return (
                day.isBefore(minDate, 'day') ||
                day.isAfter(maxDate, 'day') ||
                is_blackout ||
                currentCheckout?.pickup_settings?.blackout_days?.includes(
                  day.day(),
                )
              );
            }}
          />
        </div>
        <div className="relative">
          <div
            className="absolute inset-0 flex items-center"
            aria-hidden="true"
          >
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center">
            {/* <span className="bg-white px-2 text-sm text-gray-500">Continue</span> */}
          </div>
        </div>
        <div className="mt-4">
          <div className="flex flex-row justify-left content-center items-start mb-2">
            <label
              className=" block uppercase tracking-wide text-on-surface text-sm font-bold"
              htmlFor="booking_time_slots"
            >
              Pickup Time Slots
            </label>
          </div>
          <Spin
            spinning={fetchSlots}
            tip={'Fetching available slots'}
            indicator={LoadingIcon}
            className=""
          >
            {!fetchSlots &&
            currentCheckout?.pickup_settings?.blackout_days?.includes(
              moment().day(),
            ) ? (
              <div className="text-on-surface">
                No ordering slots available to order right now. You can continue
                to browse the catalog.
              </div>
            ) : !fetchSlots && bookingDate && bookingSlots.length === 0 ? (
              <div className="text-on-surface">
                No ordering slots available to order right now. You can continue
                to browse the catalog.
              </div>
            ) : (
              <RadioGroup
                value={selectedSlot}
                onChange={slot => {
                  setSelectSlot(slot);
                  // onSelect(slot);
                  if (store.getState().cart.pickup_details) {
                    dispatch(
                      setPickupDetails({
                        [r_id]: {
                          ...store.getState().cart.pickup_details[r_id],
                          schedule_type: 'scheduled',
                          scheduled_for: moment(
                            slot.date + slot?.start_at,
                            'YYYY-MM-DD HH:mm',
                          ).toISOString(),
                        },
                      }),
                    );
                    dispatch(
                      setFulfillmentDetails({
                        [r_id]: {
                          ...store.getState().cart.fulfillment_details[r_id],
                          type: 'pick_up',
                          order_item_application: 'all_items',
                          pickup_details: {
                            ...store.getState().cart.pickup_details[r_id],
                            schedule_type: 'scheduled',
                            note: '',
                            scheduled_for: moment(
                              slot.date + slot?.start_at,
                              'YYYY-MM-DD HH:mm',
                            ).toISOString(),
                          },
                        },
                      }),
                    );
                  } else {
                    dispatch(
                      setPickupDetails({
                        [r_id]: {
                          schedule_type: 'scheduled',
                          scheduled_for: moment(
                            slot.date + slot?.start_at,
                            'YYYY-MM-DD HH:mm',
                          ).toISOString(),
                        },
                      }),
                    );
                    dispatch(
                      setFulfillmentDetails({
                        [r_id]: {
                          type: 'pick_up',
                          order_item_application: 'all_items',
                          pickup_details: {
                            schedule_type: 'scheduled',
                            note: '',
                            scheduled_for: moment(
                              slot.date + slot?.start_at,
                              'YYYY-MM-DD HH:mm',
                            ).toISOString(),
                          },
                        },
                      }),
                    );
                  }
                }}
              >
                <RadioGroup.Label className="sr-only">
                  {' '}
                  Pickup Slot{' '}
                </RadioGroup.Label>
                <div className="space-y-4">
                  {bookingSlots.map(plan => (
                    <RadioGroup.Option
                      key={plan.start_at}
                      disabled={plan.bookings_remaining <= 0}
                      value={plan}
                      className={({ checked, active }) =>
                        classNames(
                          checked ? 'border-transparent' : 'border-gray-300',
                          active
                            ? 'border-primary border-1 ring-2 ring-primary'
                            : '',
                          'relative block cursor-pointer rounded-lg border bg-surface py-2 shadow-sm focus:outline-none sm:flex sm:justify-between',
                        )
                      }
                    >
                      {({ active, checked }) => (
                        <>
                          <span
                            className={`flex items-center  ${
                              checked ? 'px-1' : 'px-2'
                            }`}
                          >
                            {checked && (
                              <CheckCircleIcon className="text-primary w-4 h-4 mr-2" />
                            )}
                            <span className="flex flex-col text-sm  ">
                              <RadioGroup.Label
                                as="span"
                                className="font-medium text-on-surface flex flex-row justify-start"
                              >
                                {moment(plan.start_at, 'HH:mm:ss').format(
                                  'hh:mm a',
                                )}{' '}
                                -{' '}
                                {moment(plan.end_at, 'HH:mm:ss').format(
                                  'hh:mm a',
                                )}
                              </RadioGroup.Label>
                            </span>
                          </span>
                          <span
                            className={classNames(
                              active ? 'border' : 'border-2',
                              checked ? 'border-primary' : 'border-transparent',
                              'pointer-events-none absolute -inset-px rounded-lg',
                            )}
                            aria-hidden="true"
                          />
                        </>
                      )}
                    </RadioGroup.Option>
                  ))}
                </div>
              </RadioGroup>
            )}
          </Spin>
        </div>
      </div>
      {selectedSlot ? (
        <>
          <div
            className={`sticky ${
              fromCheckout ? 'bottom-16' : 'bottom-28'
            } flex justify-center items-center bg-primary p-2 rounded-md`}
          >
            <button
              type="button"
              onClick={() => {
                onSelect(selectedSlot);
              }}
              className="text-on-primary flex w-full justify-center"
            >
              Confirm
            </button>
          </div>
          {!fromCheckout ? (
            <div className="sticky bottom-16 flex justify-center items-center bg-tertiary p-2 rounded-md">
              <button
                type="button"
                onClick={() => {
                  onCancel();
                }}
                className="text-on-tertiary flex w-full justify-center"
              >
                Continue browsing
              </button>
            </div>
          ) : (
            <></>
          )}
        </>
      ) : !fromCheckout ? (
        <div className="sticky bottom-16 flex justify-center items-center bg-tertiary p-2 rounded-md">
          <button
            type="button"
            onClick={() => {
              onCancel();
            }}
            className="text-on-tertiary flex w-full justify-center"
          >
            Continue browsing
          </button>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
}
